import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import ClientAvatar from 'components/shared/ClientAvatar';
import parse from 'html-react-parser';
import { Dispatch, MouseEvent, SetStateAction, useReducer, useState } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import CustomDeleteModal from '../../CustomDeleteModal/CustomDeleteModal';
import CustomPopOver from '../../CustomPopOver/CustomPopOver';
import styles from './styles.module.css';

const CustomNoteCard = ({
  data,
  setNotes,
  setSelectedNoteId,
  deleteNotes,
  loading,
}: {
  data: any;
  setNotes: Dispatch<SetStateAction<string>>;
  setSelectedNoteId: Dispatch<SetStateAction<string>>;
  deleteNotes: (id: string) => Promise<void>;
  loading: boolean;
}) => {
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    delete: false,
  });

  const [anchorEl, setAnchorEl] = useState<HTMLImageElement | null>(null);
  const handleClick = (event: MouseEvent<HTMLImageElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const theme = useTheme();

  const handleEditNote = () => {
    setSelectedNoteId(data?.id);
    setNotes(data?.content);
    setAnchorEl(null);
  };
  const handleDeleteNote = () => {
    modalDispatch({ type: 'CLOSE', payload: 'delete' });
    deleteNotes(data.id);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '10px', xl: '16px' } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <ClientAvatar
            profilePicture={data?.profilePicture ?? ''}
            clientName={data?.createdByName}
            sx={{ width: { xs: '30px', xl: '36px' }, height: { xs: '30px', xl: '36px' } }}
          />
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '1rem',
              }}
            />
          ) : (
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              {data?.createdByName}
            </Typography>
          )}
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '1rem',
              }}
            />
          ) : (
            <Typography variant='responsiveLabel1' color={zeplinColor.Neutral8}>
              {Utilities.formatDate(data?.createdDate)} at{' '}
              {new Date(data?.createdDate).toLocaleString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </Typography>
          )}
        </Box>
        <img
          className={styles['vertical-dots']}
          src={'/assets/vertical-dots.svg'}
          alt='options-icon'
          onClick={handleClick}
          aria-hidden='false'
        />
      </Box>
      {loading ? (
        <Skeleton
          variant='text'
          sx={{
            fontSize: theme.typography.body2,
            width: '100%',
          }}
        />
      ) : (
        <Typography
          variant='responsiveHeading5'
          color={zeplinColor.BasicBlack}
          sx={{
            whiteSpace: 'wrap',
            textWrap: 'wrap',
            flexWrap: 'wrap',
            wordWrap: 'break-word',
            width: '85%',
          }}
        >
          {parse(data?.content)}
        </Typography>
      )}

      <CustomPopOver
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        id={id}
        sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '10px', xl: '16px' },
            padding: { xs: '10px', xl: '16px' },
          }}
        >
          <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleEditNote}>
            <img src={'/assets/edit.svg'} className={styles['option-icon']} alt='edit icon' />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Edit
            </Typography>
          </Box>

          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={() => {
              modalDispatch({ type: 'OPEN', payload: 'delete' });
              setAnchorEl(null);
            }}
          >
            <img
              src={'/assets/delete-red.png'}
              className={styles['option-icon']}
              alt='delete icon'
            />
            <Typography variant='responsiveBody3' color={zeplinColor.Error}>
              Delete
            </Typography>
          </Box>
        </Box>
      </CustomPopOver>

      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={() => handleDeleteNote()}
        title={''}
      />
    </Box>
  );
};

export default CustomNoteCard;
